import React from 'react';
import {
    Alert,
    Box,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Star } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { GridColDef } from '@mui/x-data-grid';
import CustomNoRowsOverlay from '../../../../../shared/NoRowsOverlayCustom';
import StripedDataGrid from '../../../../../shared/StripedDataGrid';
import FeesSection from './FeesSection';
import { DateRow, ExperienceDetails } from '../../Shared/types';
import { iconForType } from '../../Shared/iconForType';

type Props = {
    experienceDetails: ExperienceDetails;
    fullAddress: string;
    label: string;
    value: string;
    dates: DateRow[];
    columns: GridColDef[];
    availability?: {
        desks?: number;
        privateOffices?: number;
        meetingRooms?: number;
    };
    spaceType?: string;
    locationState?: any;
    isOwner: boolean;
    isOnBehalf?: boolean;
    showFees: boolean;
    fees: any;
    spaces: any[];
    checkinTime?: string;
    checkoutTime?: string;
};

const formatTimeWithAmPm = (t: unknown) => {
    const raw = String(t ?? '').trim();
    if (!raw || raw === '-') return '-';
    const m = moment(
        raw,
        ['H:mm', 'HH:mm', 'h:mm A', 'h:mma', 'hh:mm A'],
        true,
    );
    if (m.isValid()) return m.format('h:mm A');
    return raw;
};

export default function RightSummary({
    experienceDetails,
    fullAddress,
    label,
    value,
    dates,
    columns,
    availability,
    spaceType,
    locationState,
    isOwner,
    isOnBehalf,
    showFees,
    fees,
    spaces,
    checkinTime,
    checkoutTime,
}: Props) {
    const Icon = iconForType(experienceDetails?.type);
    const theme = useTheme();

    const isCommercial = experienceDetails?.type === 'commercial';
    const dateCardHeading = isCommercial ? 'Access window' : label;

    const showCommercialTimeBlock =
        isCommercial &&
        !(
            experienceDetails?.details?.subType === 'house' &&
            spaceType === 'privateOffice'
        );

    const venueTimeRangeDisplay = (() => {
        const s = formatTimeWithAmPm(experienceDetails?.checkinTime);
        const e = formatTimeWithAmPm(experienceDetails?.checkoutTime);
        if (s === '-' && e === '-') return '';
        if (s !== '-' && e !== '-') return `${s} → ${e}`;
        return s !== '-' ? s : e;
    })();

    const selectedTimeRangeDisplay = (() => {
        const s = formatTimeWithAmPm(checkinTime);
        const e = formatTimeWithAmPm(checkoutTime);
        if (s === '-' && e === '-') return '';
        if (s !== '-' && e !== '-') return `${s} → ${e}`;
        return s !== '-' ? s : e;
    })();

    const timeRangeDisplay =
        selectedTimeRangeDisplay ||
        (showCommercialTimeBlock ? venueTimeRangeDisplay : '');

    const outerCardSx = {
        width: '100%',
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        p: { xs: 2, sm: 2.5 },
    };

    const infoCardSx = {
        width: '100%',
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        backgroundColor: theme.palette.neutral.main,
        borderRadius: 2,
        p: 2,
    };

    const sectionDividerSx = {
        mt: 2,
        pt: 2,
        borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
    };

    const titleSx = {
        fontWeight: 900,
        letterSpacing: 0.2,
        fontSize: 15,
        lineHeight: 1.15,
        color: theme.palette.text.primary,
    };

    const metaRowSx = {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.text.primary,
    };

    const dateCardLabelSx = {
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.35,
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
    };

    const dateRangeSx = {
        fontSize: 14,
        fontWeight: 900,
        color: theme.palette.text.primary,
        mt: 0.75,
        lineHeight: 1.2,
    };

    const timeRangeSx = {
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.text.secondary,
        mt: 0.4,
        lineHeight: 1.2,
    };

    return (
        <Box sx={outerCardSx}>
            <Stack gap={{ xs: 1.5, sm: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: { xs: 1.5, sm: 2 },
                    }}
                >
                    <Box
                        component='img'
                        src={experienceDetails.photos?.[0]?.src}
                        alt='experiencePhoto'
                        sx={{
                            width: { xs: 56, sm: 72, md: 84 },
                            height: { xs: 56, sm: 72, md: 84 },
                            borderRadius: 1.5,
                            objectFit: 'cover',
                            display: 'block',
                            flex: '0 0 auto',
                        }}
                    />
                    <Stack gap={{ xs: 0.75, sm: 1 }} sx={{ width: '100%' }}>
                        <Typography component='h2' variant='body1' sx={titleSx}>
                            {experienceDetails?.name}
                        </Typography>

                        <Stack
                            direction='row'
                            alignItems='center'
                            flexWrap='wrap'
                            gap={{ xs: 1, sm: 2 }}
                            sx={{ rowGap: { xs: 0.5, sm: 1 } }}
                        >
                            {typeof experienceDetails?.rating === 'number' && (
                                <Typography
                                    variant='body3'
                                    sx={{
                                        ...metaRowSx,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Star
                                        sx={{
                                            fontSize: 16,
                                            color: 'text.primary',
                                        }}
                                    />
                                    {Number(
                                        experienceDetails?.rating || 0,
                                    ).toFixed(1)}
                                </Typography>
                            )}

                            <Typography
                                variant='body3'
                                sx={{
                                    ...metaRowSx,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Icon
                                    sx={{ fontSize: 16, color: 'text.primary' }}
                                />
                                {(experienceDetails?.type ?? '').replace(
                                    /^./,
                                    (c: string) => c.toUpperCase(),
                                )}
                            </Typography>

                            <Typography
                                variant='body3'
                                sx={{
                                    ...metaRowSx,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    maxWidth: { xs: '100%', sm: '60%' },
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                }}
                                title={`${experienceDetails?.suburb} ${experienceDetails?.state}, ${experienceDetails?.countryCode}`}
                            >
                                <LocationOnIcon
                                    sx={{ fontSize: 16, color: 'text.primary' }}
                                />
                                <Link
                                    style={{ color: 'inherit' }}
                                    target='_blank'
                                    to={`https://www.google.com/maps/place/${fullAddress}`}
                                >
                                    {experienceDetails?.suburb}{' '}
                                    {experienceDetails?.state},{' '}
                                    {experienceDetails?.countryCode}
                                </Link>
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>

                <Box sx={infoCardSx}>
                    <Typography sx={dateCardLabelSx}>
                        {dateCardHeading}
                    </Typography>
                    <Typography variant='body3' sx={dateRangeSx}>
                        {value}
                    </Typography>
                    {timeRangeDisplay ? (
                        <Typography sx={timeRangeSx}>
                            {timeRangeDisplay}
                        </Typography>
                    ) : null}
                </Box>

                <Box>
                    {isOwner &&
                        !isOnBehalf &&
                        (locationState?.times?.checkinFrom ||
                            locationState?.times?.checkoutBefore) && (
                            <Alert
                                severity='warning'
                                sx={{ mt: 1, borderRadius: 2 }}
                            >
                                There is an existing reservation within the
                                selected dates
                            </Alert>
                        )}
                </Box>

                {dates?.length > 1 && (
                    <Box sx={{ width: '100%', pb: { xs: 2, sm: 2 } }}>
                        <StripedDataGrid
                            getRowClassName={(p) =>
                                p.indexRelativeToCurrentPage % 2 === 0
                                    ? 'even'
                                    : 'odd'
                            }
                            sx={{
                                boxShadow: 0,
                                border: 0,
                                '& .MuiDataGrid-cell': {
                                    p: { xs: 0.5, sm: 0.75 },
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    display: { xs: 'none', sm: 'block' },
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    minHeight: { xs: 120, sm: 160 },
                                },
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            }}
                            hideFooter
                            disableColumnFilter
                            disableColumnSelector
                            disableColumnMenu
                            disableDensitySelector
                            disableRowSelectionOnClick
                            rows={dates ?? []}
                            columns={columns}
                            getRowId={(row: any) => row?.id}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                        />
                    </Box>
                )}

                {showCommercialTimeBlock && (
                    <Stack gap={1.25}>
                        {!availability ? (
                            <Skeleton
                                variant='text'
                                sx={{ fontSize: '1rem' }}
                            />
                        ) : (
                            <Box sx={infoCardSx}>
                                <Typography sx={dateCardLabelSx}>
                                    Availability on this date
                                </Typography>
                                <Typography
                                    variant='body3'
                                    sx={{
                                        ...dateRangeSx,
                                        fontWeight: 800,
                                    }}
                                >
                                    {availability?.desks! > 0 && (
                                        <>
                                            Dedicated Desks:{' '}
                                            <Box
                                                component='span'
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {availability?.desks}
                                            </Box>{' '}
                                            -{' '}
                                        </>
                                    )}
                                    {availability?.privateOffices! > 0 && (
                                        <>
                                            {experienceDetails?.details
                                                ?.subType === 'house'
                                                ? 'Rooms:'
                                                : 'Private Offices:'}{' '}
                                            <Box
                                                component='span'
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {availability?.privateOffices}
                                            </Box>{' '}
                                            -{' '}
                                        </>
                                    )}
                                    {availability?.meetingRooms! > 0 && (
                                        <>
                                            Meeting Rooms:{' '}
                                            <Box
                                                component='span'
                                                sx={{ fontWeight: 900 }}
                                            >
                                                {availability?.meetingRooms}
                                            </Box>
                                        </>
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                )}

                {showFees && (
                    <Box sx={sectionDividerSx}>
                        <FeesSection
                            show={showFees}
                            fees={fees}
                            moduleId={experienceDetails?.id}
                            moduleType={experienceDetails?.type}
                            spaces={spaces}
                        />
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
